import React, { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import Button from 'reactstrap/lib/Button';
import { get, map, sortBy } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ComparatorModel } from 'client/data/models/comparator';
import { FeatureSpecsEntities } from 'client/data/models/vehicle-v2';
import { featuresMap, tcoMap } from 'client/data/models/car-features-map';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { formatPriceString, getPriceString } from 'site-modules/shared/utils/price-utils';
import { getStyleName } from 'site-modules/shared/utils/features-specs/utils';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { fixTableCellValue } from 'site-modules/shared/utils/voiceover';
import { OVERVIEW_FEATURES_MAP } from 'site-modules/shared/constants/build-price';
import { CarColorSection } from 'site-modules/shared/components/comparator/car-color-section/car-color-section';
import { ContentTable } from 'site-modules/shared/components/comparator/content-table/content-table';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';

function CompareGridUI({
  stylesFeatures = null,
  styleFilter,
  comparableFeatures = [],
  tco = [],
  colors = [],
  pathname = null,
  isMobile = false,
}) {
  const [isStickyHeader, setStickyHeader] = useState(false);

  const onScroll = useCallback(
    ({ target: { scrollTop } }) => {
      const isSticky = scrollTop > 0;

      if (isSticky === isStickyHeader) {
        return;
      }

      setStickyHeader(isSticky);
    },
    [isStickyHeader]
  );

  if (!stylesFeatures) {
    return null;
  }

  const renderMap = [
    {
      isThead: true,
      features: [
        {
          srOnly: true,
          data: stylesFeatures.map(({ name }) => getStyleName(name).style),
        },
      ],
    },
    {
      alternateFeaturesBgColor: false,
      sectionClassName: 'car-header bg-white',
      features: [
        {
          rowClassName: 'car-action-row small',
          rowLabel: 'Style name',
          data: stylesFeatures.map(({ name }) => {
            const { style, engine } = getStyleName(name);

            return (
              <Fragment>
                <h3 className="heading-6 mb-0_5">{style}</h3>
                <div className="small">({engine})</div>
              </Fragment>
            );
          }),
        },
        {
          rowLabel: 'Price',
          data: stylesFeatures.map(({ price }) => (
            <Fragment>
              <div
                className={classnames({
                  'heading-4 mb-0_25': !isStickyHeader,
                  'heading-5 d-inline-block': isStickyHeader,
                })}
              >
                {getPriceString(get(price, 'baseMSRP'))}&nbsp;
              </div>
              <div className="small d-inline-block">starting MSRP</div>
            </Fragment>
          )),
        },
        {
          cellClassName: 'py-0',
          rowLabel: 'Choose style',
          data: stylesFeatures.map(({ name, id: styleId }) => (
            <Button
              tag="a"
              className="build-btn px-1 py-0_5 mb-0_5"
              color="outline-primary-b"
              href={`${pathname}?${objectToQueryString({
                modelyearId: styleFilter.modelyearId,
                submodel: styleFilter.submodel,
                styleId,
              })}`}
              data-tracking-id="build_price_select_from_compare_widget"
              data-tracking-value={name}
            >
              Build this <span className="visually-hidden"> - {name}</span>
            </Button>
          )),
        },
      ],
    },
    {
      heading: 'Overview',
      headingClassName: 'heading-6',
      features: OVERVIEW_FEATURES_MAP.map(({ name, path, fieldName, fallbackFieldName }) => ({
        label: name,
        data: stylesFeatures.map(styleDetails => {
          const feature = get(styleDetails, path, []);
          const value = fieldName
            ? get(
                feature.find(({ name: featureName }) => featureName === fieldName || featureName === fallbackFieldName),
                'value',
                'N/A'
              )
            : feature;

          return <ContentFragment classes="feature-value">{fixTableCellValue(value)}</ContentFragment>;
        }),
      })),
    },
    ...featuresMap
      .filter(({ isEvSection }) => !isEvSection)
      .map(({ values, name: categoryName }) => ({
        heading: categoryName,
        headingClassName: 'heading-6',
        features: values
          .filter(({ camelName }) => camelName)
          .map(({ name }) => ({
            label: name,
            data: comparableFeatures.map(({ [categoryName]: { [name]: value } }) => (
              <ContentFragment classes="feature-value">{fixTableCellValue(value)}</ContentFragment>
            )),
          })),
      })),
    {
      heading: '5-Year Ownership Costs',
      headingClassName: 'heading-6',
      features: map(tcoMap, (name, niceName) => ({
        label: name,
        data: tco.map(tcoObj =>
          typeof get(tcoObj, `["${niceName}"]`) === 'number'
            ? formatPriceString(tcoObj[niceName], 0, 2)
            : 'Not available'
        ),
      })),
    },
    {
      alternateFeaturesBgColor: false,
      heading: 'Colors',
      headingClassName: 'heading-6',
      features: [
        {
          label: 'Interior',
          data: colors.map(({ INTERIOR }, index) => (
            <CarColorSection idPrefix={`veh-${index}`} type="interior" colors={INTERIOR} />
          )),
        },
        {
          label: 'Exterior',
          data: colors.map(({ EXTERIOR }, index) => (
            <CarColorSection idPrefix={`veh-${index}`} type="exterior" colors={EXTERIOR} />
          )),
        },
      ],
    },
  ];

  return (
    <div
      className={classnames('compare-models-grid pb-3_5 pb-md-2 px-0_5 px-md-1', { sticky: isStickyHeader })}
      onScroll={onScroll}
    >
      <div className="pb-3 pb-md-0">
        <ContentTable
          renderMap={renderMap}
          sectionHeadingTag="h4"
          keyPrefix="features"
          sharedClassNames={{ rowClassName: 'small' }}
          colNumber={stylesFeatures.length}
          isSmall={isMobile}
        />
      </div>
    </div>
  );
}

CompareGridUI.propTypes = {
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity),
  styleFilter: BuildPriceEntities.StyleFilter.isRequired,
  comparableFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      exteriorFeatures: PropTypes.objectOf(PropTypes.string),
      interiorFeatures: PropTypes.objectOf(PropTypes.string),
      safety: PropTypes.objectOf(PropTypes.string),
      specifications: PropTypes.objectOf(PropTypes.string),
      warranty: PropTypes.objectOf(PropTypes.string),
    })
  ),
  tco: PropTypes.arrayOf(
    PropTypes.shape({
      federalTaxCredit: PropTypes.number,
      insurance: PropTypes.number,
      maintenance: PropTypes.number,
      repairs: PropTypes.number,
      taxesAndFees: PropTypes.number,
      financeInterest: PropTypes.number,
      depreciation: PropTypes.number,
      fuel: PropTypes.number,
      tcoPrice: PropTypes.number,
      averageCostPerMile: PropTypes.number,
    })
  ),
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      EXTERIOR: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          color: PropTypes.shape({
            r: PropTypes.number,
            g: PropTypes.number,
            b: PropTypes.number,
          }),
        })
      ),
      INTERIOR: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          color: PropTypes.shape({
            r: PropTypes.number,
            g: PropTypes.number,
            b: PropTypes.number,
          }),
        })
      ),
    })
  ),
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  pathname: get(state, 'pageContext.location.pathname', ''),
});

export const CompareGrid = connect(mapStateToProps)(
  connectToModel(CompareGridUI, {
    stylesFeatures: bindToPath(
      ({ styleIdList }) => BuildPricePaths.buildStylesFeaturesPath(styleIdList),
      BuildPriceModel,
      stylesFeatures => sortBy(stylesFeatures, ({ price }) => get(price, 'baseMSRP', 0))
    ),
    comparableFeatures: bindToPath(({ styleIdList }) => `comparableFeatures["${styleIdList}"]`, ComparatorModel),
    tco: bindToPath(({ styleIdList }) => `tco["${styleIdList}"]`, ComparatorModel),
    colors: bindToPath(({ styleIdList }) => `colors["${styleIdList}"]`, ComparatorModel),
  })
);
